import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import type { Amenity, AvailabilitySchedule, CreateAmenityRequest } from '@/types/v2/amenity';
import { createAmenity } from '@/api/amenities';
import { ProfessionalDialog } from '@/components/common';
import { toast } from '@/components/core/v2/toaster';
import { useUser } from '@/hooks/v2/use-user';
import { useConnectStatus } from '@/hooks/v2/use-connect-status';

import { AmenityForm, buildCreateDefaults, DAYS_OF_WEEK } from './amenity-form';
import type { AmenityFormValues } from './amenity-form';
import { amenitySchema } from './amenity-schema';

interface CreateAmenityDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (amenity: Amenity) => void;
}

export function CreateAmenityDialog({ open, onClose, onSuccess }: CreateAmenityDialogProps): React.JSX.Element {
  const { community } = useUser();
  const { isReady: stripeReady, paymentsEnabled } = useConnectStatus(community?.id);
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AmenityFormValues>({
    resolver: zodResolver(amenitySchema),
    defaultValues: buildCreateDefaults(),
  });

  // Reset the form every time the dialog opens fresh
  React.useEffect(() => {
    if (open) {
      reset(buildCreateDefaults());
    }
  }, [open, reset]);

  const mutation = useMutation({
    mutationFn: (data: CreateAmenityRequest) => createAmenity(community?.id || '', data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['amenities', community?.id] });
      toast.success('Amenity created');
      reset();
      onSuccess(created);
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to create amenity');
    },
  });

  const onSubmit = (data: AmenityFormValues) => {
    const availability_schedule: AvailabilitySchedule = {};
    for (const day of DAYS_OF_WEEK) {
      const entry = data.availability[day];
      if (entry?.enabled) {
        availability_schedule[day] = { start: entry.start, end: entry.end };
      }
    }

    const required_documents = (data.required_documents ?? []).map((s) => s.trim()).filter(Boolean);

    mutation.mutate({
      community_id: community?.id || '',
      name: data.name,
      description: data.description,
      rules: data.rules,
      fee_description: data.fee_description,
      deposit_amount: data.deposit_amount,
      total_fee_amount: data.total_fee_amount,
      required_documents: required_documents.length > 0 ? required_documents : undefined,
      availability_schedule: Object.keys(availability_schedule).length > 0 ? availability_schedule : null,
      seasonal_start_date: data.seasonal_enabled ? data.seasonal_start_date : null,
      seasonal_end_date: data.seasonal_enabled ? data.seasonal_end_date : null,
      time_block_duration: data.time_block_duration,
      reservation_min_duration: data.reservation_min_duration ?? null,
      reservation_max_duration: data.reservation_max_duration ?? null,
      max_guests: data.max_guests ?? null,
      guest_count_required: data.guest_count_required,
      alcohol_question_enabled: data.alcohol_question_enabled,
      requires_approval: data.requires_approval,
      is_active: data.is_active,
    });
  };

  return (
    <ProfessionalDialog
      open={open}
      onClose={onClose}
      title="Add Amenity"
      subtitle={community?.name ? `New amenity for ${community.name}` : 'New amenity'}
      maxWidth="md"
      actions={
        <>
          <Button onClick={onClose} variant="outlined" disabled={mutation.isPending}>
            Cancel
          </Button>
          <LoadingButton
            loading={mutation.isPending || isSubmitting}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
          >
            Create Amenity
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <AmenityForm control={control} errors={errors} mode="create" paymentsEnabled={paymentsEnabled} stripeReady={stripeReady} />
      </form>
    </ProfessionalDialog>
  );
}
